import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, withModifiers as _withModifiers, withKeys as _withKeys } from "vue"

const _hoisted_1 = { class: "mt-6 mb-0 space-y-4 px-0" }
const _hoisted_2 = ["role", "tabindex", "aria-expanded", "onClick", "onKeydown"]
const _hoisted_3 = { class: "flex flex-col text-left" }
const _hoisted_4 = { class: "text-md leading-6 font-poppins text-black font-bold p-0 mt-2 mb-0" }
const _hoisted_5 = { class: "flex flex-col text-left font-poppins text-black leading-6 p-0 m-0 mt-1" }
const _hoisted_6 = { class: "p-0 m-0" }
const _hoisted_7 = {
  key: 0,
  class: "p-0 m-0 mt-1"
}

import { OutageData } from '@/services/integration/clients/types';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/vue/outline';
import { computed, ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';

type OutagesList = {
  outages: Array<OutageData> | null
  type: 'current' | 'scheduled'
}


export default /*@__PURE__*/_defineComponent({
  __name: 'OutageList',
  props: {
    outages: {},
    type: {}
  },
  setup(__props: any) {

const props = __props;

const plannedEndLabel = computed(() => (
  props.type === 'scheduled' ? 'Estimated outage completion' : 'Estimated restoration date'
));

const expandedIds = ref(new Set<string>());
const truncatedIds = ref(new Set<string>());
const titleRefs = new Map<string, HTMLElement>();

const isExpanded = (id: string) => expandedIds.value.has(id);
const isTruncated = (id: string) => truncatedIds.value.has(id);

const toggle = (id: string) => {
  if (!isTruncated(id)) return;
  const next = new Set(expandedIds.value);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  expandedIds.value = next;
};

const setTitleRef = (el: HTMLElement | null, id: string) => {
  if (el) {
    titleRefs.set(id, el);
  } else {
    titleRefs.delete(id);
  }
};

// Only re-measures collapsed titles: an expanded title isn't clamped, so its
// scrollHeight/clientHeight comparison can't tell us whether it was truncated.
const recomputeTruncation = () => {
  const next = new Set<string>();
  titleRefs.forEach((el, id) => {
    if (isExpanded(id)) {
      if (truncatedIds.value.has(id)) next.add(id);
      return;
    }
    if (el.scrollHeight > el.clientHeight + 1) {
      next.add(id);
    }
  });
  truncatedIds.value = next;
};

let resizeTimeout: ReturnType<typeof setTimeout> | undefined;
const onResize = () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(recomputeTruncation, 150);
};

onMounted(() => {
  recomputeTruncation();
  window.addEventListener('resize', onResize);
});

onBeforeUnmount(() => {
  clearTimeout(resizeTimeout);
  window.removeEventListener('resize', onResize);
});

watch(() => props.outages, () => nextTick(recomputeTruncation));


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", null, [
      _createElementVNode("dl", _hoisted_1, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.outages, (outage) => {
          return (_openBlock(), _createElementBlock("div", {
            key: outage.id,
            class: _normalizeClass(["relative text-left border border-solid border-black rounded-lg p-6", { 'pr-14 cursor-pointer': isTruncated(outage.id) }]),
            role: isTruncated(outage.id) ? 'button' : undefined,
            tabindex: isTruncated(outage.id) ? 0 : undefined,
            "aria-expanded": isTruncated(outage.id) ? isExpanded(outage.id) : undefined,
            onClick: ($event: any) => (toggle(outage.id)),
            onKeydown: [
              _withKeys(_withModifiers(($event: any) => (toggle(outage.id)), ["prevent"]), ["enter"]),
              _withKeys(_withModifiers(($event: any) => (toggle(outage.id)), ["prevent"]), ["space"])
            ]
          }, [
            _createElementVNode("dt", _hoisted_3, [
              _createElementVNode("p", {
                ref_for: true,
                ref: (el) => setTitleRef(el as HTMLElement | null, outage.id),
                class: _normalizeClass(["text-lg leading-6 font-poppins text-black p-0 m-0", { 'line-clamp-3': !isExpanded(outage.id) }])
              }, _toDisplayString(outage.title), 3),
              _createElementVNode("p", _hoisted_4, "Network - " + _toDisplayString(outage.network), 1)
            ]),
            _createElementVNode("dd", _hoisted_5, [
              _createElementVNode("p", _hoisted_6, _toDisplayString(outage.status), 1),
              (outage.plannedEndAt)
                ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(plannedEndLabel.value) + " - " + _toDisplayString(outage.plannedEndAt), 1))
                : _createCommentVNode("", true)
            ]),
            (isTruncated(outage.id))
              ? (_openBlock(), _createBlock(_resolveDynamicComponent(isExpanded(outage.id) ? _unref(ChevronDownIcon) : _unref(ChevronRightIcon)), {
                  key: 0,
                  class: "absolute top-6 right-6 h-5 w-5 text-black",
                  "aria-hidden": "true"
                }))
              : _createCommentVNode("", true)
          ], 42, _hoisted_2))
        }), 128))
      ])
    ])
  ]))
}
}

})